'use client';

import { useAtomValue } from 'jotai';
import { useCallback, useMemo, useState } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { Datetime } from '@flipster/react/design-utils/datetime';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { center, hstack, stack } from '@flipster/styled-system/patterns';
import { formatToShortNumber } from '@flipster/utils/num';

import { TrendChart, type ChartDataPoint } from '../../../../../shared/trend-chart';
import { buildHistoryChartData } from '../../../chart-data';
import { useProductConfig } from '../../product-config';

export const TvlChart = () => {
  const { detailsAtom } = useProductConfig();
  const details = useAtomValue(detailsAtom);

  const todayTvl = Number(details?.totalValueLocked ?? '0');
  const chartData = useMemo<ChartDataPoint[]>(
    () =>
      buildHistoryChartData(
        details?.totalValueLockedHistory ?? [],
        (item) => Number(item.totalValueLocked),
        30,
        todayTvl,
      ),
    [details?.totalValueLockedHistory, todayTvl],
  );

  const [hoveredPoint, setHoveredPoint] = useState<ChartDataPoint | null>(null);

  const handleHover = useCallback((point: ChartDataPoint | null) => {
    setHoveredPoint(point);
  }, []);

  const displayValue = hoveredPoint?.value ?? Number(details?.totalValueLocked ?? '0');

  const formatYAxisLabel = useCallback((value: number) => formatToShortNumber(String(value)), []);

  return (
    <>
      <div className={hstack({ justify: 'space-between' })}>
        <div className={stack({ gap: 4 })}>
          <p className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-2' })}>TVL</p>
          <p className={css({ textStyle: 'Title/S/Monospace', color: 'content.default.level-5' })}>
            <Formatted.ShortNumber value={displayValue} suffix='USDT' />
          </p>
        </div>
        <p
          className={center({
            direction: 'row',
            py: 8,
            px: 12,
            rounded: 1000,
            bg: 'background.accent.secondary.tint',
            textStyle: 'Text/M/Regular',
            color: 'content.default.level-5',
          })}
        >
          <FormattedMessage id='Common.r251107.days_days' values={{ days: 30 }} />
        </p>
      </div>

      <div className={css({ h: 200, tabletDown: { h: 160 } })}>
        <TrendChart data={chartData} onHover={handleHover} formatYAxisLabel={formatYAxisLabel} />
      </div>

      <p className={css({ textStyle: 'Text/XS/Regular', color: 'content.default.level-1', textAlign: 'right' })}>
        <FormattedMessage
          id='Common.r251107.Latest_update_colon_date_time'
          values={{
            dateTime: (
              <Datetime
                key='dateTime'
                value={details?.totalValueLockedLatestUpdate}
                format='yyyy.MM.dd, HH:mm'
                isUTC
                withUTCText
              />
            ),
          }}
        />
      </p>
    </>
  );
};
